import { useEffect, type ReactNode, type Ref, type RefObject } from "react";
import classNames from "classnames";
import { TriangleFilledIcon } from "@icons";
import { resolveChevronMode, usePickerRoot } from "./PickerContext";
import { PickerTrigger } from "./PickerTrigger";
import type { PickerPopupProps } from "./Picker.types";
import styles from "./PickerAttachedTrigger.module.scss";

/** Placement of the attached popup: flush on the host's top edge, as wide as the host. */
export const ATTACHED_POPUP_PROPS: Partial<PickerPopupProps> = {
    side: "top",
    align: "start",
    sideOffset: 0,
    matchTriggerWidth: false,
    className: styles.attachedPopup,
};

export interface PickerAttachedTriggerProps {
    /** The host box the popup anchors to. */
    attachedTo: RefObject<HTMLElement | null>;
    /** Heads the popup; also leads the strip's accessible name. */
    title: string;
    icon?: ReactNode;
    placeholder?: ReactNode;
    className?: string;
    ref?: Ref<HTMLButtonElement>;
}

/** A combobox takes no name from its content, so the strip always names itself: title, then value. */
const attachedTriggerName = ({
    title,
    valueName,
}: {
    title: string;
    valueName: string | undefined;
}) => (valueName === undefined ? title : `${title}, ${valueName}`);

/**
 * The `attached` preset's trigger: a chromeless full-width strip (leading
 * icon, label, caret) on the edge of a host box, whose tint shows through.
 * Hands the host to the root as the popup anchor once refs are attached, and
 * again on every open in case the host remounted.
 */
export const PickerAttachedTrigger = ({
    attachedTo,
    title,
    icon,
    placeholder = "Select",
    className,
    ref,
}: PickerAttachedTriggerProps) => {
    const root = usePickerRoot();
    const { setTriggerAnchor, isPopupOpen } = root;
    useEffect(() => {
        setTriggerAnchor(attachedTo.current);
    }, [attachedTo, setTriggerAnchor, isPopupOpen]);
    useEffect(() => () => setTriggerAnchor(null), [setTriggerAnchor]);

    const chevronMode = resolveChevronMode({ showChevron: undefined, root });
    const hasValue = root.selectionLabel !== null;
    const label = root.selectionNode ?? root.selectionLabel ?? placeholder;
    const accessibleName = attachedTriggerName({
        title,
        valueName:
            root.selectionLabel ??
            (typeof placeholder === "string" ? placeholder : undefined),
    });

    return (
        <PickerTrigger
            ref={ref}
            type="button"
            className={classNames(styles.attachedTrigger, className)}
            aria-label={accessibleName}
            aria-invalid={root.isInvalid || undefined}
            data-chevron={chevronMode}
            data-placeholder={hasValue ? undefined : ""}
            data-forced-state={root.forcedState}
            onBlur={root.onTriggerBlur}
        >
            {icon !== undefined && (
                <span className={styles.attachedIcon} aria-hidden>
                    {icon}
                </span>
            )}
            <span className={styles.attachedLabel}>{label}</span>
            {chevronMode !== "none" && (
                <TriangleFilledIcon
                    className={styles.attachedCaret}
                    data-picker-chevron=""
                    aria-hidden
                />
            )}
        </PickerTrigger>
    );
};

/** Heading row of the attached popup: the title that names the listbox and an upward caret. */
export const PickerAttachedTitle = ({
    id,
    children,
}: {
    id: string;
    children: ReactNode;
}) => {
    const { closePopup } = usePickerRoot();
    return (
        // Pointer-only (Escape is the keyboard path), so the list stays the popup's tab stop.
        <button
            type="button"
            tabIndex={-1}
            aria-expanded
            className={styles.attachedTitle}
            onClick={closePopup}
        >
            <span id={id} className={styles.attachedTitleText}>
                {children}
            </span>
            <TriangleFilledIcon
                className={styles.attachedTitleCaret}
                aria-hidden
            />
        </button>
    );
};

/** The attached preset's slice of the list content: placement, title row and the listbox's name. */
export const attachedListContentProps = ({
    title,
    titleId,
}: {
    title: string;
    titleId: string;
}) => ({
    popupProps: ATTACHED_POPUP_PROPS,
    header: <PickerAttachedTitle id={titleId}>{title}</PickerAttachedTitle>,
    listLabelledBy: titleId,
});
